import type {
  Booking,
  Customer360,
  CustomerActivity,
  CustomerActivityInput,
  CustomerSegment,
  CustomerSegmentInput,
  CustomerSummary,
  NotificationPreferences
} from "@kuroo/contracts";

export type SdkCustomerContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function customers(context: SdkCustomerContext, search?: string): Promise<CustomerSummary[]> {
  const query = search ? `?q=${encodeURIComponent(search)}` : "";
  return context.request<CustomerSummary[]>(`/api/v1/customers${query}`);
}

export function customer(context: SdkCustomerContext, customerId: string): Promise<Customer360 & { bookings: Booking[] }> {
  return context.request<Customer360 & { bookings: Booking[] }>(`/api/v1/customers/${encodeURIComponent(customerId)}`);
}

export function updateCustomer(context: SdkCustomerContext, customerId: string, input: { firstName?: string; lastName?: string; phone?: string; timezone?: string; locale?: string }): Promise<CustomerSummary & { bookings: Booking[] }> {
  return context.request<CustomerSummary & { bookings: Booking[] }>(`/api/v1/customers/${encodeURIComponent(customerId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function updateCustomerProfile(context: SdkCustomerContext, customerId: string, input: { note?: string | null; tags?: string[] }): Promise<Customer360 & { bookings: Booking[] }> {
  return context.request<Customer360 & { bookings: Booking[] }>(`/api/v1/customers/${encodeURIComponent(customerId)}/profile`, { method: "PATCH", body: JSON.stringify(input) });
}

export function customerActivities(context: SdkCustomerContext, customerId: string): Promise<CustomerActivity[]> {
  return context.request<CustomerActivity[]>(`/api/v1/customers/${encodeURIComponent(customerId)}/activities`);
}

export function createCustomerActivity(context: SdkCustomerContext, customerId: string, input: CustomerActivityInput): Promise<CustomerActivity> {
  return context.request<CustomerActivity>(`/api/v1/customers/${encodeURIComponent(customerId)}/activities`, { method: "POST", body: JSON.stringify(input) });
}

export function updateCustomerActivity(context: SdkCustomerContext, customerId: string, activityId: string, input: { status: "open" | "completed" }): Promise<CustomerActivity[]> {
  return context.request<CustomerActivity[]>(`/api/v1/customers/${encodeURIComponent(customerId)}/activities/${encodeURIComponent(activityId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function customerSegments(context: SdkCustomerContext): Promise<CustomerSegment[]> {
  return context.request<CustomerSegment[]>("/api/v1/customer-segments");
}

export function createCustomerSegment(context: SdkCustomerContext, input: CustomerSegmentInput): Promise<CustomerSegment> {
  return context.request<CustomerSegment>("/api/v1/customer-segments", { method: "POST", body: JSON.stringify(input) });
}

export function customerNotificationPreferences(context: SdkCustomerContext, customerId: string): Promise<NotificationPreferences> {
  return context.request<NotificationPreferences>(`/api/v1/customers/${encodeURIComponent(customerId)}/notification-preferences`);
}

export function updateCustomerNotificationPreferences(context: SdkCustomerContext, customerId: string, input: Partial<Omit<NotificationPreferences, "userId" | "updatedAt">>): Promise<NotificationPreferences> {
  return context.request<NotificationPreferences>(`/api/v1/customers/${encodeURIComponent(customerId)}/notification-preferences`, { method: "PATCH", body: JSON.stringify(input) });
}
